/**
 * inline-creation-utils.ts
 *
 * Lógica pura para la creación inline de entidades desde OrdenFormDialog.
 * Sin dependencias React — diseñado para ser testeado con PBT (fast-check).
 *
 * Feature: inline-entity-creation-orden
 */

// =============================================================================
// TYPES
// =============================================================================

export interface InlineMedicoFormData {
  nombre_completo: string;
  especialidad?: string;
  celular?: string;
}

export interface InlineClienteFormData {
  nombre: string;
  rtn?: string;
  direccion?: string;
  contacto_nombre?: string;
  contacto_telefono?: string;
}

export interface OrdenFormState {
  pacienteId: number | null;
  examenes: number[];
  prioridad: string;
  medicoId: number | null;
  clienteId: number | null;
}

// =============================================================================
// FUNCTIONS
// =============================================================================

/**
 * Determina si debe mostrarse la opción de creación inline en un combobox.
 * Retorna true solo si el usuario tiene permiso Y hay texto de búsqueda no vacío.
 *
 * Property 1: shouldShowCreateOption(p, q) === true iff p === true && q.trim().length > 0
 */
export function shouldShowCreateOption(hasPermission: boolean, searchQuery: string): boolean {
  return hasPermission && searchQuery.trim().length > 0;
}

/**
 * Genera el valor inicial del formulario inline a partir del texto de búsqueda.
 * Función identidad: retorna searchQuery sin modificación.
 *
 * Property 2: getInlineFormInitialName(s) === s para todo s
 */
export function getInlineFormInitialName(searchQuery: string): string {
  return searchQuery;
}

/**
 * Valida los datos del formulario de médico remitente inline.
 * Retorna null si válido, o un objeto con errores por campo.
 *
 * Property 3: rechaza nombre vacío o < 2 chars no-whitespace; acepta ≥ 2
 */
export function validateInlineMedicoForm(
  data: InlineMedicoFormData
): Record<string, string> | null {
  const errors: Record<string, string> = {};

  const trimmed = data.nombre_completo?.trim() ?? "";
  if (trimmed.length < 2) {
    errors.nombre_completo =
      trimmed.length === 0
        ? "ordenes.inline.validation.nameRequired"
        : "ordenes.inline.validation.nameMin";
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

/**
 * Valida los datos del formulario de cliente inline.
 * Retorna null si válido, o un objeto con errores por campo.
 *
 * Property 3: rechaza nombre vacío o < 2 chars no-whitespace; acepta ≥ 2
 * Property 4: valida RTN condicionalmente
 */
export function validateInlineClienteForm(
  data: InlineClienteFormData
): Record<string, string> | null {
  const errors: Record<string, string> = {};

  const trimmed = data.nombre?.trim() ?? "";
  if (trimmed.length < 2) {
    errors.nombre =
      trimmed.length === 0
        ? "ordenes.inline.validation.nameRequired"
        : "ordenes.inline.validation.nameMin";
  }

  const rtnError = validateRTN(data.rtn);
  if (rtnError) {
    errors.rtn = rtnError;
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

/**
 * Valida el formato de RTN hondureño (13-14 dígitos numéricos).
 * Retorna null si válido o vacío, mensaje de error en caso contrario.
 *
 * Property 4: null para vacío/undefined o /^\d{13,14}$/; error para todo lo demás
 */
export function validateRTN(rtn: string | undefined): string | null {
  if (!rtn || rtn.trim() === "") return null;
  if (/^\d{13,14}$/.test(rtn)) return null;
  return "ordenes.inline.validation.rtnFormat";
}

/**
 * Determina el listaPrecioId a auto-seleccionar dado un cliente recién creado.
 * Proyección directa del campo lista_precio.
 *
 * Property 6: retorna lista_precio si !== null, null en caso contrario
 */
export function getAutoListaPrecioFromCliente(cliente: {
  lista_precio: number | null;
}): number | null {
  return cliente.lista_precio ?? null;
}

/**
 * Construye el payload de creación de cliente inline.
 * Garantiza que sucursal siempre sea sucursalId sin modificación.
 *
 * Property 7: payload.sucursal === sucursalId para cualquier sucursalId positivo
 */
export function buildClientePayload(
  formData: InlineClienteFormData,
  sucursalId: number
): InlineClienteFormData & { sucursal: number } {
  return {
    ...formData,
    sucursal: sucursalId,
  };
}

/**
 * Simula la cancelación de un formulario inline.
 * Retorna el estado previo sin modificación (idempotente).
 *
 * Property 5: cancelInlineForm(state) deep-equals state para cualquier state
 */
export function cancelInlineForm(prevState: OrdenFormState): OrdenFormState {
  return { ...prevState };
}
